import * as React from "react"

import { http } from "@/lib/http"

import type { ReviewId } from "../../data/review-data"
import type { WireReport } from "../report-data"

/** Who the exported file is for. A client is always given the client view, whatever is asked. */
export type ExportAudience = "client" | "reviewer"
export type ExportFormat = "pdf" | "csv" | "json"

/**
 * The JSON export's filename, as the legacy console names it. The server names
 * the PDF and CSV itself; the JSON is saved here, so it is named here.
 */
export function jsonExportName(
  report: Pick<WireReport, "reg_nr" | "version_no">,
  audience: ExportAudience,
): string {
  const suffix = audience === "reviewer" ? "-reviewer" : ""
  return `pyxisrc-review-${report.reg_nr}-v${String(report.version_no)}${suffix}.json`
}

/**
 * Hand the browser a file it already has in memory.
 *
 * The object URL is revoked on the next tick rather than inline: the click is
 * still being acted on when the line after it runs, and a report PDF is large
 * enough for that to matter.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 0)
}

export interface ReportExport {
  /** The format being prepared; every export button waits while one is. */
  pending: ExportFormat | null
  download: (format: ExportFormat, audience: ExportAudience) => void
}

/**
 * The report's three downloads: the server-rendered PDF and CSV, and the
 * report object itself as JSON.
 *
 * All three are fetched when asked for rather than built from the screen's
 * cached report: the cache holds the report reshaped for the screen, and the
 * PDF, the CSV and the JSON must be the same object the server renders.
 * `onError` is told when one fails — a download that silently produces
 * nothing is indistinguishable from one the browser swallowed.
 */
export function useReportExport(
  reviewId: ReviewId,
  onError: (error: unknown, format: ExportFormat) => void,
): ReportExport {
  const [pending, setPending] = React.useState<ExportFormat | null>(null)
  const onErrorRef = React.useRef(onError)
  React.useLayoutEffect(() => {
    onErrorRef.current = onError
  })

  const download = React.useCallback(
    (format: ExportFormat, audience: ExportAudience) => {
      const path = `/api/v1/reviews/${encodeURIComponent(reviewId)}/report`
      setPending(format)
      const run = async (): Promise<void> => {
        if (format === "json") {
          const wire = await http.get<WireReport>(path, { params: { view: audience } })
          saveBlob(
            new Blob([JSON.stringify(wire, null, 2)], { type: "application/json" }),
            jsonExportName(wire, audience),
          )
          return
        }
        const file = await http.download(path, `review-${reviewId}-${audience}.${format}`, {
          params: { format, view: audience },
        })
        saveBlob(file.blob, file.filename)
      }
      run()
        .catch((error: unknown) => {
          onErrorRef.current(error, format)
        })
        .finally(() => {
          setPending(null)
        })
    },
    [reviewId],
  )

  return { pending, download }
}
